/* eslint-disable */
import React, { Component } from "react";
import { confirmWrapper } from "src/components/Dialog";
import { RowSelectionState } from "@tanstack/react-table";
import { AxiosError } from "axios";
import withRouter from "../../hooks/withRouter";
import API from "../../network";
import {
  JobRequisitionProps,
  JobRequisitionState,
  JobRequisitionViewRendererProps,
  UpdateJobsStatusRequest,
  PostOnWebsiteProps,
  UpdateInterviewPositionProps,
  ConfigureInterviewProps,
  SendInternalReferenceLinkProps,
  JobRequisitionJobListRequestBody,
} from "./types";
import ViewRenderer from "./views";
import ChangeStatusPopup from "./components/ChangeStatusPopup";
import { CreateJob } from "./components";
import Utils from "./utils";
import ConfigureInterview from "./components/ConfigureInterview";
import { JobListFiltersSubmitHandler } from "./components/JobListFilters";
import moment from "moment";

class JobRequisition extends Component<
  JobRequisitionProps,
  JobRequisitionState
> {
  constructor(props: JobRequisitionProps) {
    super(props);

    this.state = {
      jobsFetching: false,
      loading: false,
      jobs: [],
      rowSelection: {},
      filters: {
        pagination: {
          pageIndex: 0,
          pageSize: 100,
          totalRecords: 0,
        },
      },
    };
  }

  updateJobStatus = async (
    data: UpdateJobsStatusRequest,
    close: (() => void) | undefined
  ): Promise<void> => {
    try {
      await API.getInstance().updateJobsStatus(data);
      const message = "Job status updated successfully!";
      await confirmWrapper(message, {
        hideSecondaryBtn: true,
        hidePrimaryBtn: true,
        variant: "success",
        autoClose: true,
      });
      this.setState({
        rowSelection: {},
      })
      this.fetchJobs();
      if (close) close();
    } catch (err) {
      await confirmWrapper("Something went wrong", {
        hideSecondaryBtn: true,
        variant: "danger",
      });
    }
  };

  createJob = async (
    requestFormData: FormData,
    close: (() => void) | undefined
  ): Promise<void> => {
    const confirmation = await confirmWrapper(
      "Are you sure you want to create new Job?",
      {
        variant: "warning",
        primaryBtnLabel: "Submit",
        secondaryBtnLabel: "Cancel",
      }
    );
    this.setState(
      {
        jobsFetching: true,
      },
      async () => {
        if (confirmation) {
          try {
            await API.getInstance().createJob(requestFormData);
            await confirmWrapper("Job created successfully", {
              variant: "success",
              primaryBtnLabel: "ok",
              hideSecondaryBtn: true,
              autoClose: true,
            });
            this.fetchJobs();
            if (close) close();
          } catch (e: unknown) {
            if (e instanceof AxiosError && e.response?.status === 409) {
              await confirmWrapper(e.response.data, {
                hideSecondaryBtn: true,
                variant: "danger",
              });
            } else {
              await confirmWrapper("Something went wrong", {
                hideSecondaryBtn: true,
                variant: "danger",
              });
            }
          }
        }
      }
    );
  };

  postJobOnWebsite = async (props: PostOnWebsiteProps) => {
    const { isPosted, jobOrderId } = props;
    try {
      await API.getInstance().postJobOnWebSite(jobOrderId);
      await this.fetchJobs();
      const message = isPosted
        ? "Job removed successfully!"
        : "Job posted successfully!";
      await confirmWrapper(message, {
        hideSecondaryBtn: true,
        hidePrimaryBtn: true,
        variant: "success",
        autoClose: true,
      });
    } catch (err) {
      await confirmWrapper("Something went wrong", {
        hideSecondaryBtn: true,
        variant: "danger",
      });
    }
  };

  updateInterviewPosition = async (
    data: UpdateInterviewPositionProps,
    close: (() => void) | undefined
  ): Promise<void> => {
    try {
      const { configurePosition, jobOrderId } = data;
      const finalData = {
        jobOrderId,
        interviewPositionId: configurePosition,
      };
      await API.getInstance().updateInterviewPosition(finalData);
      const message = "Interview position configured successfully!";
      await confirmWrapper(message, {
        hideSecondaryBtn: true,
        hidePrimaryBtn: true,
        variant: "success",
        autoClose: true,
      });
      this.fetchJobs();
      if (close) close();
    } catch (err) {
      await confirmWrapper("Something went wrong", {
        hideSecondaryBtn: true,
        variant: "danger",
      });
    }
  };

  shareInternalLink = async (
    data: SendInternalReferenceLinkProps
  ): Promise<void> => {
    try {
      await API.getInstance().shareInternalLink(data);
      const message = "Internal link mail sent successfully!";
      await confirmWrapper(message, {
        hideSecondaryBtn: true,
        hidePrimaryBtn: true,
        variant: "success",
        autoClose: true,
      });
    } catch (err) {
      await confirmWrapper("Something went wrong", {
        hideSecondaryBtn: true,
        variant: "danger",
      });
    }
  };

  showChangeStatusPopup = async (jobOrderId: number[]) => {
    await confirmWrapper("", {
      variant: "",
      hidePrimaryBtn: true,
      hideSecondaryBtn: true,
      maxWidth: "sm",
      fullWidth: true,
      containerClasses: "p-0 px-10",
      showCloseButton: true,
      disableBackdropClickClose: true,
      disableEscapeKeyDownClose: true,
      dialogContent: (
        <ChangeStatusPopup
          jobOrderId={jobOrderId}
          updateJobStatus={this.updateJobStatus}
        />
      ),
    });
    this.fetchJobs();
  };

  changeMultiStatus = async (data: RowSelectionState): Promise<void> => {
    const { jobs } = this.state;
    const selectedJobIds = Object.keys(data)
      .map(Number)
      .filter((index) => data[index]);
    const selectedJobs = jobs.filter((job, index) =>
      selectedJobIds.includes(index)
    );
    const selectedJobIdsArray: number[] = [];
    selectedJobs.forEach((job) => selectedJobIdsArray.push(job.jobOrderId));
    this.showChangeStatusPopup(selectedJobIdsArray);
  };

  showConfigureInterviewPopup = async (data: ConfigureInterviewProps) => {
    const { jobOrderId, interviewPositionId } = data;
    await confirmWrapper("", {
      variant: "",
      hidePrimaryBtn: true,
      hideSecondaryBtn: true,
      maxWidth: "sm",
      fullWidth: true,
      containerClasses: "p-0 px-10",
      showCloseButton: true,
      disableBackdropClickClose: true,
      disableEscapeKeyDownClose: true,
      dialogContent: (
        <ConfigureInterview
          jobOrderId={jobOrderId}
          interviewPositionId={interviewPositionId}
          updateInterviewPosition={this.updateInterviewPosition}
        />
      ),
    });
  };

  onRowSelectionChange = (data: RowSelectionState) => {
    this.setState({ rowSelection: data });
  };

  showCreateJobPopup = async () => {
    await confirmWrapper("", {
      variant: "",
      hidePrimaryBtn: true,
      hideSecondaryBtn: true,
      maxWidth: "md",
      fullWidth: true,
      containerClasses: "p-0",
      showCloseButton: true,
      disableBackdropClickClose: true,
      disableEscapeKeyDownClose: true,
      dialogContent: (
        <CreateJob
          onCreateJob={ async(data, close) => {
            const request = Utils.prepareCreateJobRequestObject(data as any);
            await this.createJob(request, close);
          }}
        />
      ),
    });
  };

  fetchJobs = async () => {
    this.setState({
      jobsFetching: true,
    });
    const { filters } = this.state;
    const { pagination, jobRequisition } = filters;

    const requestBody: JobRequisitionJobListRequestBody = {
      page: pagination.pageIndex,
      size: pagination.pageSize.toString(),
      ...jobRequisition,
    };

    try {
      const jobList = await API.getInstance().filterJobs(requestBody);
      this.setState({
        jobs: jobList,
      });
    } catch (e) {
      await confirmWrapper("Something went wrong", {
        hideSecondaryBtn: true,
        variant: "danger",
      });
    } finally {
      this.setState({
        jobsFetching: false,
      });
    }
  };

  onApplyFilter = (data: JobListFiltersSubmitHandler) => {
    const { filters } = this.state;
    const { values } = data;
    const {
      dateRange,
      searchQuery,
      clients,
      jobs,
      location,
      recruiter,
      status,
      assignedToMe,
    } = values;

    const startDate = dateRange?.startDate
      ? moment(dateRange?.startDate).format("MM-DD-YYYY")
      : undefined;
    const endDate = dateRange?.endDate
      ? moment(dateRange?.endDate).format("MM-DD-YYYY")
      : undefined;
    const clientId = clients?.length > 0 ? clients : undefined;

    this.setState(
      {
        jobs: [],
        filters: {
          ...filters,
          jobRequisition: {
            searchQuery,
            startDate,
            endDate,
            jobOrderId: jobs ? [jobs] : undefined,
            clientId,
            status: status ? [status] :undefined,
            location: location,
            recruiter: recruiter? recruiter.toString() : undefined,
            jobType: assignedToMe ? "Assigned": undefined,
          },
        },
      },
      () => {
        this.fetchJobs();
      }
    );
  };

  onFiltersReset = (isATSRecruiter: boolean) => {
    const { filters, jobsFetching } = this.state;
    if (jobsFetching) {
      return;
    }
    this.setState(
      {
        filters: {
          ...filters,
          jobRequisition: {
            jobType: isATSRecruiter? "Assigned": undefined,
            status: ["ACTIVE"],
          },
        },
        jobs: [],
      },
      () => {
        this.fetchJobs();
      }
    );
  };

  getProps = (): JobRequisitionViewRendererProps => {
    const { jobsFetching, jobs, rowSelection, loading, } = this.state;

    return {
      jobsFetching,
      jobs,
      rowSelection,
      loading,
      onRowSelectionChange: this.onRowSelectionChange,
      changeMultiStatus: this.changeMultiStatus,
      showConfigureInterviewPopup: this.showConfigureInterviewPopup,
      showCreateJob: this.showCreateJobPopup,
      postJobOnWebsite: this.postJobOnWebsite,
      shareInternalLink: this.shareInternalLink,
      onApplyFilter: this.onApplyFilter,
      onFiltersReset: this.onFiltersReset,
    };
  };

  render() {
    const viewProps: JobRequisitionViewRendererProps = this.getProps();

    return <ViewRenderer {...viewProps} />;
  }
}

export default withRouter(JobRequisition);
